import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Button } from 'reactstrap'
import { makePostRequest, makeLeadsGetRequest, makeLeadsPostRequest } from 'utils/api'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { getPaymentOverdueDate } from 'utils/paymentService'
import s from './Imports.scss'

const Imports = () => {
  const isVisible = !getPaymentOverdueDate()

  const [companiesFile, setCompaniesFile] = useState('')
  const [contactsFile, setContactsFile] = useState('')
  const [opportunitiesFile, setOpportunitiesFile] = useState('')
  const [leadsFile, setLeadsFile] = useState('')
  const [leadsEnabled, setLeadsEnabled] = useState(false)

  const reduxDispatch = useDispatch()

  useEffect(() => {
    ;(async () => {
      const { moduleEnabled } = await makeLeadsGetRequest('/v1/settings')
      setLeadsEnabled(moduleEnabled)
    })()
  }, [])

  const chooseFile = (e, dataType) => {
    const file = e.target.files[0]

    const fr = new FileReader()
    fr.onload = () => {
      if (dataType === 'companies') {
        setCompaniesFile(file)
      } else if (dataType === 'contacts') {
        setContactsFile(file)
      } else if (dataType === 'leads') {
        setLeadsFile(file)
      } else {
        setOpportunitiesFile(file)
      }
    }
    if (file) {
      fr.readAsDataURL(file)
    }
  }

  const uploadFile = async dataType => {
    let uploadUrl
    const formData = new FormData()

    if (dataType === 'companies') {
      uploadUrl = '/v1/companies/import'
      formData.append('file', companiesFile)
    } else if (dataType === 'contacts') {
      uploadUrl = '/v1/contacts/import'
      formData.append('file', contactsFile)
    } else if (dataType === 'leads') {
      uploadUrl = '/v1/leads/import'
      formData.append('file', leadsFile)
    } else {
      uploadUrl = '/v1/deals/import'
      formData.append('file', opportunitiesFile)
    }

    try {
      if (dataType === 'leads') {
        await makeLeadsPostRequest(uploadUrl, formData)
      } else {
        await makePostRequest(uploadUrl, formData)
      }
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: `File uploaded successfuly. The import has started successfully
          , you will receive an email with more details when the import finishes.`,
          level: 'success',
          autoDismiss: 20
        }
      })
    } catch (error) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem uploading file',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <h5>Import Contacts and Companies</h5>
      <p className={s.description}>
        Download this Excel spreadsheet and fill it out to import Contacts and Companies. Contacts
        are identified by their email address. If that email already exists, the contact won&apos;t
        be imported. Companies are identified by name. If a Company with that name already exists, a
        new one won&apos;t be created, but the Contact will be added to the existing Company.
      </p>
      <a
        className={s.link}
        href="https://ideal-import-samples.s3.us-east-2.amazonaws.com/Import+Contacts+and+Companies.xlsx"
      >
        Excel Template to Import Contacts and Companies - bold columns are required
      </a>
      <div className={s.row}>
        <span className={s.label}>Import Contacts and Companies</span>
        {isVisible && (
          <>
            <input
              type="file"
              onChange={e => chooseFile(e, 'contacts')}
              className={s.uploadInput}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />
            <Button
              className={s.saveBtn}
              onClick={() => uploadFile('contacts')}
              disabled={!contactsFile}
            >
              Upload
            </Button>
          </>
        )}
      </div>
      <h5>Import Companies Only</h5>
      <p className={s.description}>
        Download this Excel spreadsheet and fill it out to only import client companies. Companies
        are identified by name and coty. If a Company with that name already exists in that city, a
        new one won&apos;t be created. But if you have two companies with the same name but
        different cities, they will both be added.
      </p>
      <a
        className={s.link}
        href="https://ideal-import-samples.s3.us-east-2.amazonaws.com/Import+Companies+Only.xlsx"
      >
        Excel Template to Import Companies only - bold columns are required
      </a>
      <div className={s.row}>
        <span className={s.label}>Import Companies Only</span>
        {isVisible && (
          <>
            <input
              type="file"
              onChange={e => chooseFile(e, 'companies')}
              className={s.uploadInput}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />
            <Button
              className={s.saveBtn}
              onClick={() => uploadFile('companies')}
              disabled={!companiesFile}
            >
              Upload
            </Button>
          </>
        )}
      </div>
      <h5>Import Opportunities</h5>
      <p className={s.description}>
        Download this Excel spreadsheet and fill it out to import opportunities.
      </p>
      <a
        className={s.link}
        href="https://ideal-import-samples.s3.us-east-2.amazonaws.com/Import+Opportunities.xlsx"
      >
        Excel Template to Import Opportunities - bold columns are required
      </a>
      <div className={s.row}>
        <span className={s.label}>Import Opportunities</span>
        {isVisible && (
          <>
            <input
              type="file"
              onChange={e => chooseFile(e, 'opportunities')}
              className={s.uploadInput}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />
            <Button
              className={s.saveBtn}
              onClick={() => uploadFile('opportunities')}
              disabled={!opportunitiesFile}
            >
              Upload
            </Button>
          </>
        )}
      </div>
      {leadsEnabled && (
        <>
          <h5>Import Leads</h5>
          <p className={s.description}>
            Download this Excel spreadsheet and fill it out to import leads.
          </p>
          <a
            className={s.link}
            href="https://ideal-import-samples.s3.us-east-2.amazonaws.com/Import_leads.xlsx"
          >
            Excel Template to Import Leads - bold columns are required
          </a>
          <div className={s.row}>
            <span className={s.label}>Import Leads</span>
            {isVisible && (
              <>
                <input
                  type="file"
                  onChange={e => chooseFile(e, 'leads')}
                  className={s.uploadInput}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />
                <Button
                  className={s.saveBtn}
                  onClick={() => uploadFile('leads')}
                  disabled={!leadsFile}
                >
                  Upload
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}

export default Imports
